import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import AISuggestionSection from '../AISuggestionSection'
import { useTranslation } from 'react-i18next'
import AI from '../icons/AI'
import { Grid } from '@material-ui/core'
import NotifyPracticeForm from 'components/Patients/NotifyPractice/NotifyPracticeForm'
import { NotifyPracticeReasons } from 'consts/giConsts'
import { trackEvent } from 'utils/analyticsUtils'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import LinkButton from 'components/common/buttons/LinkButton'
import NotifyPracticeIcon from './icons/NotifyPracticeIcon'
import { AsyncStatus } from 'consts'
import usePatientTags from 'hooks/usePatientTags'
import { ActionTypes } from 'consts/hiToolsConsts'

const useStyles = makeStyles(theme => ({
  notifyPracticeIcon: {
    paddingRight: '0px !important'
  },
  noteInput: {
    height: '72px !important'
  }
}))

const AINotifyPractice = ({ filteredCategoriesByTrackingDetails, selectedConditionDetails = '' }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { patient } = useSelector(state => state.patientsReducer)
  const practiceName = useSelector(state => state.multiPracticeReducer?.context?.practiceName)
  const savingStatus = useSelector(state => state.hiReducer.notifyPractice.savingStatus)

  const { needsAttentionTag } = usePatientTags({ patient })

  const [note, setNote] = useState(selectedConditionDetails.join(', '))
  const [reason, setReason] = useState(NotifyPracticeReasons.Clinical)
  const [selectedPracticeMembers, setSelectedPracticeMembers] = useState([])
  const [wasPracticeNotified, setWasPracticeNotified] = useState(false)

  const hasAnyNotifyPracticeActionsInPG = useMemo(
    () =>
      Object.values(filteredCategoriesByTrackingDetails).some(categoryData =>
        Object.values(categoryData.items).some(itemValues => itemValues?.[ActionTypes.NotifyPractice]?.isChecked)
      ),
    [filteredCategoriesByTrackingDetails]
  )

  const isSubmitButtonDisabled =
    (reason === NotifyPracticeReasons.SpecificMembersAttention && !selectedPracticeMembers.length) ||
    !!needsAttentionTag

  const handleSubmit = useCallback(() => {
    const selectedPracticeMembersIds = selectedPracticeMembers.map(memberOption => memberOption.value)

    trackEvent('Notify Practice - submit info', {
      reason,
      patientId: patient?.id,
      practiceName,
      source: 'AIForHI',
      selectedPracticeMembersIds,
      withNote: !!note,
      noteLength: note?.length
    })
    dispatch(
      Actions.hiNotifyPractice({
        patientId: patient?.id,
        reason,
        note,
        selectedPracticeMembersIds
      })
    )
    setReason(NotifyPracticeReasons.Clinical)
    setSelectedPracticeMembers([])
    setNote('')
    setWasPracticeNotified(true)
  }, [dispatch, note, patient, practiceName, reason, selectedPracticeMembers])

  return (
    hasAnyNotifyPracticeActionsInPG && (
      <AISuggestionSection
        icon={<AI />}
        title={t('pages.patients.selectedPatient.chat.gi.AINotifyPractice.title')}
        completedTitle={t('pages.patients.selectedPatient.chat.gi.AINotifyPractice.completedTitle')}
        wasActionCompleted={wasPracticeNotified}
      >
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <NotifyPracticeForm
              note={note}
              onChangeNote={setNote}
              reason={reason}
              reasons={NotifyPracticeReasons}
              onChangeReason={setReason}
              selectMemberInputFullWidth
              selectedPracticeMembers={selectedPracticeMembers}
              setSelectedPracticeMembers={setSelectedPracticeMembers}
              noteInputClassName={classes.noteInput}
            />
          </Grid>
          <Grid item>
            <LinkButton
              bold
              keepTextWhileLoading
              fontSize={14}
              disabled={isSubmitButtonDisabled}
              isLoading={savingStatus === AsyncStatus.Loading}
              label={t('pages.patients.selectedPatient.chat.gi.AINotifyPractice.notifyPractice')}
              icon={<NotifyPracticeIcon />}
              onClick={handleSubmit}
              iconContainerClassName={classes.notifyPracticeIcon}
            />
          </Grid>
        </Grid>
      </AISuggestionSection>
    )
  )
}

export default AINotifyPractice
